import type { UserOrganization } from "@shared/types";
import { App, Form } from "antd";
import { faceEnrollmentService } from "@/modules/face-enrollment/services/face-enrollment.service";
import { FormModal } from "@/shared/components/FormModal";
import { useCreateStaff, useUpdateStaff } from "../hooks/useStaff";
import { useStaffStore } from "../store/staff.store";
import type { CreateStaffRequest, StaffFormValues, UpdateStaffRequest } from "../types";
import { StaffForm } from "./StaffForm";

interface StaffFormModalProps {
	organizationId: string;
	organizations: UserOrganization[];
	brigadeId?: string;
}

export function StaffFormModal({ organizationId, organizations, brigadeId }: StaffFormModalProps) {
	const [form] = Form.useForm<StaffFormValues>();
	const { modalOpen, editingItem, closeModal } = useStaffStore();
	const { message } = App.useApp();

	const { mutate: createStaff, isPending: isCreating } = useCreateStaff();
	const { mutate: updateStaff, isPending: isUpdating } = useUpdateStaff();

	const handleClose = () => {
		closeModal();
		form.resetFields();
	};

	const handleUpdate = (values: Partial<StaffFormValues>) => {
		if (!editingItem) {
			return;
		}
		const updateData: UpdateStaffRequest = {};
		const valuesRecord = values as Record<string, unknown>;
		const editingRecord = editingItem as unknown as Record<string, unknown>;
		const fields: (keyof UpdateStaffRequest)[] = [
			"fullname",
			"phoneNumber",
			"photoId",
			"jobEntryDate",
			"positionId",
			"userId",
			"staffType",
			"brigadeId",
			"maxHoursPerWeek",
			"maxHoursPerDay",
			"isActive",
			"password",
		];
		for (const key of fields) {
			// Skip if form didn't provide value (undefined = field not touched)
			if (valuesRecord[key] === undefined) {
				continue;
			}
			const nullableKeys = ["positionId", "userId", "photoId", "brigadeId"];
			const newVal = nullableKeys.includes(key) ? (valuesRecord[key] ?? null) : valuesRecord[key];
			if (newVal !== editingRecord[key]) {
				(updateData as Record<string, unknown>)[key] = newVal;
			}
		}

		// Checked days (number[]) → workdays; preserve existing ids, use form workStart/workEnd
		const checkedDays = values.workdays as number[] | undefined;
		if (Array.isArray(checkedDays)) {
			const workdayByDay = new Map<number, NonNullable<typeof editingItem.workdays>[number]>();
			for (const w of editingItem.workdays ?? []) {
				if (w.dayNumber != null && !workdayByDay.has(w.dayNumber)) {
					workdayByDay.set(w.dayNumber, w);
				}
			}
			const workStart = values.workStart ?? "08:00:00";
			const workEnd = values.workEnd ?? "18:00:00";
			updateData.workdays = checkedDays.map((dayNumber) => {
				const existing = workdayByDay.get(dayNumber);
				return {
					id: existing?.id,
					dayNumber,
					start: workStart,
					end: workEnd,
					isWorkday: true,
				};
			});
		}

		updateStaff({ id: editingItem.id, data: updateData }, { onSuccess: handleClose });
	};

	const handleSubmit = () => {
		form.validateFields().then((values) => {
			const payload: CreateStaffRequest = {
				...values,
				organizationId: values.organizationId ?? organizationId,
				// Brigade context: always send brigadeId + staffType for create (required by backend)
				...(brigadeId && !editingItem ? { brigadeId, staffType: "EMPLOYEE" as const } : {}),
			};
			if (editingItem) {
				handleUpdate(payload);
			} else {
				createStaff(payload, {
					onSuccess: (createdStaff, variables) => {
						// Auto face enrollment when staff created with photo (required for brigade employees)
						const photoId = variables.photoId ?? createdStaff.photoId;
						if (photoId) {
							faceEnrollmentService
								.create({
									staffId: createdStaff.id,
									faceImageId: photoId,
									organizationId: createdStaff.organizationId,
									ownerType: "staff",
								})
								.then(() => {
									if (brigadeId) {
										message.success("Yuz ro'yxatga olish muvaffaqiyatli qo'shildi");
									}
								})
								.catch(() => {
									if (brigadeId) {
										message.warning(
											"Yuz ro'yxatga olishda xatolik — keyinroq qayta urinib ko'ring"
										);
									}
								});
						}
						handleClose();
					},
				});
			}
		});
	};

	return (
		<FormModal
			open={modalOpen}
			editing={!!editingItem}
			title="Yangi xodim"
			editTitle="Xodimni tahrirlash"
			onCancel={handleClose}
			onOk={handleSubmit}
			confirmLoading={isCreating || isUpdating}
			width={720}
		>
			<StaffForm
				form={form}
				organizationId={editingItem?.organizationId ?? organizationId}
				organizations={organizations.map((o) => ({ id: o.id, name: o.name }))}
				hideOrganization={organizations.length <= 1}
				brigadeId={brigadeId}
			/>
		</FormModal>
	);
}
